import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { APIResponseWrapper } from '../models/common/api-response-wrapper';
import { SessionStorageService } from '../services/session-storage.service';
import {
  WASenderAccount,
  WASenderAccountTestResponse,
  WASenderConversation,
  WASenderMessage,
  WASenderSendMessageRequest,
  WASenderSendMessageResponse
} from './wasender.model';

@Injectable({ providedIn: 'root' })
export class WASenderService {
  private readonly http = inject(HttpClient);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly baseUrl = `${environment.restApiUrl}wasender`;

  getCompanyId(): number {
    return this.sessionStorageService.getCompany()?.id || this.sessionStorageService.getCustomer()?.company?.id || 0;
  }

  getCustomerId(): number {
    return this.sessionStorageService.getCustomer()?.id || 0;
  }

  getAccounts(companyId = this.getCompanyId()): Observable<WASenderAccount[]> {
    return this.http.get<APIResponseWrapper<WASenderAccount[]>>(`${this.baseUrl}/accounts`, {
      params: new HttpParams().set('companyId', companyId)
    }).pipe(map(response => response.data || []));
  }

  saveAccount(account: WASenderAccount): Observable<WASenderAccount> {
    const payload: WASenderAccount = {
      ...account,
      companyId: account.companyId || this.getCompanyId()
    };
    const request = account.id
      ? this.http.put<APIResponseWrapper<WASenderAccount>>(`${this.baseUrl}/accounts/${account.id}`, payload)
      : this.http.post<APIResponseWrapper<WASenderAccount>>(`${this.baseUrl}/accounts`, payload);
    return request.pipe(map(response => response.data));
  }

  setDefaultAccount(accountId: number, companyId = this.getCompanyId()): Observable<WASenderAccount> {
    return this.http.post<APIResponseWrapper<WASenderAccount>>(`${this.baseUrl}/accounts/${accountId}/default`, {}, {
      params: new HttpParams().set('companyId', companyId)
    }).pipe(map(response => response.data));
  }

  testAccount(accountId: number, companyId = this.getCompanyId()): Observable<WASenderAccountTestResponse> {
    let params = new HttpParams()
      .set('companyId', companyId)
      .set('senderCustomerId', this.getCustomerId());
    return this.http.post<APIResponseWrapper<WASenderAccountTestResponse>>(`${this.baseUrl}/accounts/${accountId}/test`, {}, { params })
      .pipe(map(response => response.data));
  }

  getConversations(accountId?: number | null, searchText?: string, companyId = this.getCompanyId()): Observable<WASenderConversation[]> {
    let params = new HttpParams().set('companyId', companyId);
    if (accountId) params = params.set('accountId', accountId);
    if (searchText?.trim()) params = params.set('searchText', searchText.trim());
    return this.http.get<APIResponseWrapper<WASenderConversation[]>>(`${this.baseUrl}/conversations`, { params })
      .pipe(map(response => response.data || []));
  }

  getMessages(conversationId: number, page = 0, size = 100, companyId = this.getCompanyId()): Observable<WASenderMessage[]> {
    const params = new HttpParams()
      .set('companyId', companyId)
      .set('page', page)
      .set('size', size);
    return this.http.get<APIResponseWrapper<WASenderMessage[]>>(`${this.baseUrl}/conversations/${conversationId}/messages`, { params })
      .pipe(map(response => response.data || []));
  }

  sendMessage(request: WASenderSendMessageRequest): Observable<WASenderSendMessageResponse> {
    const payload: WASenderSendMessageRequest = {
      ...request,
      companyId: request.companyId || this.getCompanyId(),
      senderCustomerId: request.senderCustomerId || this.getCustomerId()
    };
    return this.http.post<APIResponseWrapper<WASenderSendMessageResponse>>(`${this.baseUrl}/messages/send`, payload)
      .pipe(map(response => response.data));
  }

  sendMultipartMessage(request: WASenderSendMessageRequest, files: File[]): Observable<WASenderSendMessageResponse> {
    const formData = new FormData();
    formData.append('companyId', String(request.companyId || this.getCompanyId()));
    formData.append('senderCustomerId', String(request.senderCustomerId || this.getCustomerId()));
    this.appendIfPresent(formData, 'accountId', request.accountId);
    this.appendIfPresent(formData, 'conversationId', request.conversationId);
    this.appendIfPresent(formData, 'to', request.to);
    this.appendIfPresent(formData, 'contactName', request.contactName);
    this.appendIfPresent(formData, 'text', request.text);
    files.forEach(file => formData.append('files', file, file.name));
    return this.http.post<APIResponseWrapper<WASenderSendMessageResponse>>(`${this.baseUrl}/messages/send-multipart`, formData)
      .pipe(map(response => response.data));
  }

  private appendIfPresent(formData: FormData, key: string, value: string | number | null | undefined): void {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      formData.append(key, String(value));
    }
  }
}
